import { apiService } from './apiService';

export interface DependencyInfo {
  entityType: string;
  entityId: string;
  entityName: string;
  usageType: 'direct' | 'reference';
  description: string;
}

export interface DeletionCheckResult {
  canDelete: boolean;
  dependencies: DependencyInfo[];
  message: string;
}

export class DependencyService {
  /**
   * Check if an entity can be deleted and return dependency information
   */
  static async checkDeletionDependencies(
    entityType: string,
    entityId: string
  ): Promise<DeletionCheckResult> {
    try {
      const response = await apiService.get(`/dependencies/check/${entityType}/${entityId}`);
      return response.data;
    } catch (error) {
      console.error('Error checking dependencies:', error);
      return {
        canDelete: false,
        dependencies: [],
        message: 'Failed to check dependencies. Please try again.'
      };
    }
  }

  /**
   * Get formatted dependency information for display
   */
  static formatDependencies(dependencies: DependencyInfo[]): string {
    if (dependencies.length === 0) {
      return 'No dependencies found.';
    }

    const grouped = dependencies.reduce((acc, dep) => {
      if (!acc[dep.entityType]) {
        acc[dep.entityType] = [];
      }
      acc[dep.entityType].push(dep);
      return acc;
    }, {} as Record<string, DependencyInfo[]>);

    const sections = Object.entries(grouped).map(([type, deps]) => {
      const count = deps.length;
      const examples = deps.slice(0, 3).map(d => d.entityName).join(', ');
      const more = deps.length > 3 ? ` and ${deps.length - 3} more` : '';
      
      return `${type}: ${count} item${count > 1 ? 's' : ''} (${examples}${more})`;
    });

    return sections.join('\n');
  }

  /**
   * Get entity type display name
   */
  static getEntityTypeDisplayName(entityType: string): string {
    const displayNames: Record<string, string> = {
      'customers': 'Customer',
      'suppliers': 'Supplier',
      'employees': 'Employee',
      'products': 'Product',
      'categories': 'Product Category',
      'brands': 'Brand',
      'types': 'Product Type',
      'compressorTypes': 'Compressor Type',
      'countries': 'Country',
      'locations': 'Location',
      'units': 'Unit',
      'projects': 'Project',
      'invoices': 'Invoice',
      'purchaseInvoices': 'Purchase Invoice',
      'deliveryInvoices': 'Delivery Invoice',
      'returnInvoices': 'Return Invoice'
    };

    return displayNames[entityType] || entityType;
  }

  /**
   * Get usage type display name
   */
  static getUsageTypeDisplayName(usageType: string): string {
    return usageType === 'direct' ? 'Direct Usage' : 'Referenced';
  }
}
